import CloseIcon from '@mui/icons-material/Close';
import { Box, Button, Grid, Modal, Theme, Typography } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRef } from 'react';
import { splitTextByWord } from '../../../utils/Utilities';

export interface GenericModalData {
  title: string;
  description: string;
  primaryButtonText: string;
  secondaryButtonText: string;
  boldText?: string; // The modal needs to support showing description text with only one word in bold style. This variable is to support it.
  theme: Theme;
  verticalPositionForMobile?: string;
  alertBoxStyle?: {
    distributionDirection?: string;
    contentJustifyType: string;
    alignmentType: string;
    spacing: number;
    primaryActionButtonStyle: {
      backgroundColor: string;
      borderColor: string;
      color: string;
      hoverBackgroundColor: string;
      hoverTextColor: string;
      width: string;
      buttonOrder?: number;
    };
    secondaryActionButtonStyle?: {
      color: string;
    };
  };
  closeButtonActionHandler: () => void;
  primaryButtonActionHandler: () => void;
  secondaryButtonActionHandler: () => void;
}

export default function GenericModal(props: GenericModalData) {
  const isMobile = useMediaQuery(props.theme.breakpoints.down('sm'));
  const modalRef = useRef<HTMLDivElement>(null);
  const text = props.description;
  const boldWord = props.boldText ? props.boldText : '';
  const { parts, boldSet } = boldWord ? splitTextByWord(text, boldWord) : { parts: [], boldSet: new Set<string>() };
  const secondaryActionButtonStyle = Object.assign(
    {},
    props.theme.custom.styles.genericTertiaryButton,
    props.alertBoxStyle?.secondaryActionButtonStyle
  );

  return (
    <Modal open={true} onClose={props.closeButtonActionHandler}>
      <Box
        ref={modalRef}
        sx={{
          position: 'absolute',
          boxShadow: props.theme.shadows[5],
          left: '50%',
          top: { xs: '100px', sm: '120px', md: '150px' },
          transform: 'translate(-50%, 0)',
          backgroundColor: props.theme.custom.colors.bodyColor,
          borderRadius: props.theme.custom.styles.borderRadius.radiusModal,
          width: isMobile ? '90%' : '488px',
          minWidth: '220px',
          outline: 'none',
          overflow: 'hidden', // Add this line to clip child content
        }}
      >
        <Grid container>
          <Grid container item>
            <Grid item xs={10}>
              <Typography
                sx={{
                  ...props.theme.custom.styles.modalHeader,
                  padding: '1rem 1rem .5rem 1rem',
                }}
              >
                {props.title}
              </Typography>
            </Grid>
            <Grid item sx={{ display: 'flex', justifyContent: 'flex-end' }} xs={2}>
              <Button
                startIcon={<CloseIcon />}
                onClick={props.closeButtonActionHandler}
                sx={{
                  minWidth: 'auto',
                  px: '1rem',
                  py: '1rem',
                  color: props.theme.custom.styles.modalHeader.closeIconColor,
                  alignItems: 'baseline',
                  '&:hover': {
                    backgroundColor: 'transparent',
                  },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              {props.theme.custom.styles.underLineModalHeader && (
                <hr style={props.theme.custom.styles.modalHeaderHrStyle}></hr>
              )}
            </Grid>
            <Grid container>
              <Grid style={{ padding: '.5rem 1rem .5rem 1rem' }} item xs={12}>
                {props.boldText ? (
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {parts.map((part, index) =>
                      boldSet.has(part.toLowerCase()) ? (
                        <Typography component="span" sx={{ fontWeight: 'bold' }} key={index}>
                          {part}
                        </Typography>
                      ) : (
                        part
                      )
                    )}
                  </Typography>
                ) : (
                  <Typography variant="body1"> {props.description} </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                {props.theme.custom.styles.underLineModalDescription && (
                  <hr style={props.theme.custom.styles.modalHeaderHrStyle}></hr>
                )}
              </Grid>
              <Grid
                item
                container
                sx={{ m: '1rem' }}
                justifyContent={props.alertBoxStyle?.contentJustifyType}
                alignItems={props.alertBoxStyle?.alignmentType}
                spacing={props.alertBoxStyle?.spacing}
                direction={props.alertBoxStyle?.distributionDirection as any}
              >
                <Grid
                  item
                  sx={{
                    width: props.alertBoxStyle?.primaryActionButtonStyle.width,
                  }}
                  order={{
                    xs: props.alertBoxStyle?.primaryActionButtonStyle.buttonOrder,
                  }}
                >
                  <Button
                    sx={{
                      ...props.alertBoxStyle?.primaryActionButtonStyle,
                      p: '1rem',
                      width: props.alertBoxStyle?.primaryActionButtonStyle.width,
                    }}
                    onClick={props.primaryButtonActionHandler}
                  >
                    {props.primaryButtonText}
                  </Button>
                </Grid>
                <Grid item>
                  <Button
                    sx={{
                      ...secondaryActionButtonStyle,
                      p: '1rem',
                    }}
                    onClick={props.closeButtonActionHandler}
                  >
                    {props.secondaryButtonText}
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    </Modal>
  );
}
